import { SelectProps } from '@amzn/awsui-components-core';
import {
  AWSService,
  ContentAudience,
  ContentPriority,
  ContentSubType,
  ContentTemplateType,
  ContentType,
  Environment,
  IndependentSoftwareVendor,
  LifeCycle,
  PatternAccreditation,
  PatternProvenance,
  PatternType,
  RType,
  TaskSkill,
  TechDomain,
  Technology,
  TechWriterStatus,
  WorkflowState,
  Workload,
  PublicationIssueType,
  WorkflowTag,
  BarRaiserStatus,
  TechDomainDisplayNames,
  TechDomainDetails,
} from '@amzn/awscat-pattern-library-service-model';
import naturalCompare from 'natural-compare';
import MigrationGuideSchemaV1 from '../../template/MigrationGuide-v1.json';
import MigrationPatternSchemaV1 from '../../template/MigrationPattern-v1.json';
import MigrationPatternSchemaV2 from '../../template/MigrationPattern-v2.json';
import MigrationStrategySchemaV1 from '../../template/MigrationStrategy-v1.json';
import PatternSchemaV1 from '../../template/Pattern-v1.json';
import PatternSchemaV2 from '../../template/Pattern-v2.json';
import {
  awsServiceDisplayNames,
  awsServiceReadonlyDisplayNames,
  barRaiserStatusDisplayNames,
  contentAudienceBadgeDisplayNames,
  contentAudienceDisplayNames,
  contentAudienceDetails,
  contentPriorityDisplayNames,
  contentSubTypeDisplayNames,
  contentTypeDisplayNames,
  deprecatedAWSServices,
  environmentDisplayNames,
  isvDisplayNames,
  lifeCycleDisplayNames,
  patternAccreditationDisplayNames,
  patternProvenanceDisplayNames,
  publicationFeedbackTypeDisplayName,
  rTypeDisplayNames,
  taskSkillDisplayNames,
  technologyDisplayNames,
  techWriterStatusDisplayNames,
  workflowStateDisplayNames,
  workflowTagDisplayName,
  workloadDisplayNames,
} from './DisplayNames';
import { getTopServicesText } from './MetadataUtils';

type SelectOptions = SelectProps.Option[];

const naturalSort = (l: any[]) =>
  l.sort((a, b) => naturalCompare(a.label.toLowerCase(), b.label.toLowerCase()));

const doNotSort = (l: SelectOptions) => l;

const ContentTypeOrder = [
  ContentType.Strategy,
  ContentType.Guide,
  ContentType.Pattern,
  ContentType.Post,
  ContentType.ExternalResource,
];
const ContentAudienceOrder = [
  ContentAudience.INTERNAL,
  ContentAudience.PARTNER,
  ContentAudience.EXTERNAL,
  ContentAudience.PUBLIC,
];

const createFromEnum = (
  e: { [i: number]: string },
  m: Map<string, string> | Map<string, { name: string; severity: string }>,
  excludes: string[] = [],
  sortFn = naturalSort,
  detailsMap: Map<string, any> | undefined = undefined,
  disabled: string[] = [],
): SelectOptions => {
  return sortFn(
    Object.keys(e)
      .map((k) => {
        // @ts-expect-error TS7015 - strict migration
        if (Array.isArray(excludes) && excludes.includes(e[k])) {
          return null;
        }
        const value = m.get(k);
        const label = typeof value === 'string' ? value : value?.name || k;
        const description =
          typeof value === 'object' && value?.severity
            ? value.severity
            : // @ts-expect-error TS7015 - strict migration
              detailsMap?.get(e[k])?.topics || undefined;
        return {
          id: k,
          label,
          value: k,
          description,
          // @ts-expect-error TS7015 - strict migration
          tags: detailsMap?.get(e[k])?.services
            ? // @ts-expect-error TS7015 - strict migration
              ['Services: ' + getTopServicesText(detailsMap?.get(e[k])?.services)]
            : [],
          // @ts-expect-error TS7015 - strict migration
          disabled: !!disabled.includes(e[k]),
        };
      })
      .filter((v) => !!v),
  );
};

export const DashboardContentTypeOptions: SelectOptions = createFromEnum(
  ContentType,
  contentTypeDisplayNames,
  [ContentType.ExternalResource],
  (l: SelectOptions) =>
    l.sort(
      (a, b) =>
        ContentTypeOrder.indexOf(a.value as ContentType) -
        ContentTypeOrder.indexOf(b.value as ContentType),
    ),
);

export const ContentTypeOptions: SelectOptions = createFromEnum(
  ContentType,
  contentTypeDisplayNames,
  [ContentType.ExternalResource, ContentType.Post],
  (l: SelectOptions) =>
    l.sort(
      (a, b) =>
        ContentTypeOrder.indexOf(a.value as ContentType) -
        ContentTypeOrder.indexOf(b.value as ContentType),
    ),
);

export const ContentAudienceOptions: SelectOptions = createFromEnum(
  ContentAudience,
  contentAudienceDisplayNames,
  [ContentAudience.PARTNER, ContentAudience.EXTERNAL],
  doNotSort,
  contentAudienceDetails,
);

export const PostAudienceOptions: SelectOptions = createFromEnum(
  ContentAudience,
  contentAudienceBadgeDisplayNames,
  [ContentAudience.PUBLIC],
  (l: SelectOptions) =>
    l.sort(
      (a, b) =>
        ContentAudienceOrder.indexOf(a.value as ContentAudience) -
        ContentAudienceOrder.indexOf(b.value as ContentAudience),
    ),
);

export const DashboardWorkflowStateOptions: SelectOptions = createFromEnum(
  WorkflowState,
  workflowStateDisplayNames,
  [WorkflowState.FINALIZED],
);

export const BR_PENDING_APPROVAL_FILTER_VALUE = 'BR_PENDING_APPROVAL';
export const BR_APPROVED_FILTER_VALUE = 'BR_APPROVED';

export const BrWorkflowStateOptions: SelectOptions = [
  { value: BR_PENDING_APPROVAL_FILTER_VALUE, label: 'BR pending approval' },
  { value: BR_APPROVED_FILTER_VALUE, label: 'BR approved' },
];

export const WorkflowStateOptions: SelectOptions = createFromEnum(
  WorkflowState,
  workflowStateDisplayNames,
);

export const RTypeOptions: SelectOptions = createFromEnum(RType, rTypeDisplayNames);

export const ContentSubTypeOptions: SelectOptions = createFromEnum(
  ContentSubType,
  contentSubTypeDisplayNames,
  [ContentSubType.ReleaseNotes, ContentSubType.ComingSoon, ContentSubType.GettingStarted],
);

export const PostTypeOptions: SelectOptions = createFromEnum(
  ContentSubType,
  contentSubTypeDisplayNames,
  [
    ContentSubType.PatternCloudNative,
    ContentSubType.PatternMigration,
    ContentSubType.PatternProcess,
    ContentSubType.PatternGovernance,
    ContentSubType.PatternOptimization,
    ContentSubType.PatternSecurityAndCompliance,
    ContentSubType.GuideMigration,
    ContentSubType.StrategyMigration,
  ],
);

export const PatternTypeOptions: SelectOptions = createFromEnum(
  PatternType,
  contentSubTypeDisplayNames,
);

export const LifeCycleOptions: SelectOptions = createFromEnum(LifeCycle, lifeCycleDisplayNames);
export const PatternAccreditationOptions: SelectOptions = createFromEnum(
  PatternAccreditation,
  patternAccreditationDisplayNames,
);

export const PatternProvenanceOptions: SelectOptions = createFromEnum(
  PatternProvenance,
  patternProvenanceDisplayNames,
);

export const EnvironementOptions: SelectOptions = createFromEnum(
  Environment,
  environmentDisplayNames,
);

export const IndependentSoftwareVendorOptions: SelectOptions = createFromEnum(
  IndependentSoftwareVendor,
  isvDisplayNames,
);
export const TaskSkillOptions: SelectProps.Option[] = createFromEnum(
  TaskSkill,
  taskSkillDisplayNames,
);
export const TechnologyOptions: SelectOptions = createFromEnum(Technology, technologyDisplayNames);
export const TechDomainOptions: SelectOptions = createFromEnum(
  TechDomain,
  TechDomainDisplayNames,
  [TechDomain.NotApplicable],
  naturalSort,
  TechDomainDetails,
  [TechDomain.EngagementManagement],
);
export const WORKLOAD_NOT_APPLICABLE = 'NotApplicable';
export const WorkloadOptions: SelectOptions = [
  ...createFromEnum(Workload, workloadDisplayNames),
  {
    label: 'Not applicable',
    value: WORKLOAD_NOT_APPLICABLE,
    description: undefined,
    tags: [],
    disabled: false,
  },
];
/** Strip the UI-only "Not applicable" value from a workload array before sending to the backend. */
export const stripWorkloadNotApplicable = (workload: string[]): string[] =>
  workload.filter((w) => w !== WORKLOAD_NOT_APPLICABLE);

/** Resolve mutual exclusivity between "Not applicable" and real workload values. */
export const resolveWorkloadSelection = (
  newValues: string[],
  previousValues: string[],
): string[] => {
  const hasNA = newValues.includes(WORKLOAD_NOT_APPLICABLE);
  const hadNA = previousValues.includes(WORKLOAD_NOT_APPLICABLE);
  if (hasNA && !hadNA) return [WORKLOAD_NOT_APPLICABLE];
  if (hasNA && newValues.length > 1) return newValues.filter((v) => v !== WORKLOAD_NOT_APPLICABLE);
  return newValues;
};
export const AWSServiceOptions: SelectOptions = createFromEnum(
  AWSService,
  awsServiceDisplayNames,
  deprecatedAWSServices,
);

export const AWSServiceReadonlyOptions: SelectOptions = createFromEnum(
  AWSService,
  awsServiceReadonlyDisplayNames,
);

const severityOrder = { High: 0, Medium: 1, Low: 2, NA: 3 };

export const PublicationFeedbackTypeOptions: SelectOptions = createFromEnum(
  PublicationIssueType,
  publicationFeedbackTypeDisplayName,
  [
    PublicationIssueType.Kudos,
    PublicationIssueType.BarRaiserUpdateRequest,
    // Content Freshness Scan issue types — system-generated, not user-selectable
    PublicationIssueType.ServiceNaming,
    PublicationIssueType.MissingPrerequisite,
    PublicationIssueType.NonInclusiveTerms,
    PublicationIssueType.PassiveVoice,
    PublicationIssueType.UnprofessionalLanguage,
    PublicationIssueType.RedundantContent,
    PublicationIssueType.InconsistentTone,
    PublicationIssueType.Superlatives,
  ],
  (l: SelectOptions) =>
    l.sort((a, b) => {
      const severityA = severityOrder[a.description as keyof typeof severityOrder] ?? 999;
      const severityB = severityOrder[b.description as keyof typeof severityOrder] ?? 999;
      if (severityA !== severityB) return severityA - severityB;
      // @ts-expect-error TS18048 - strict migration
      return naturalCompare(a.label.toLowerCase(), b.label.toLowerCase());
    }),
);

export const TechWriterStatusOptions: SelectOptions = createFromEnum(
  TechWriterStatus,
  techWriterStatusDisplayNames,
  [],
  doNotSort,
);

export const BarRaiserStatusOptions = (forPublishedContents: boolean): SelectOptions => {
  return createFromEnum(
    BarRaiserStatus,
    barRaiserStatusDisplayNames,
    forPublishedContents ? [] : [BarRaiserStatus.UnpublishingRecommended],
    doNotSort,
  );
};

const createFromNumericEnum = (
  e: { [i: number]: string },
  m: Map<number, string>,
  excludes: string[] = [],
  sortFn = naturalSort,
): SelectOptions => {
  return sortFn(
    Object.values(e)
      .filter((o) => typeof o === 'number')
      .map((k) =>
        // @ts-expect-error TS2345 - strict migration
        Array.isArray(excludes) && excludes.includes(e[k])
          ? null
          : {
              id: k,
              label: m.get(Number(k)) || k,
              value: k,
            },
      )
      .filter((v) => !!v),
  );
};

export const ContentPriorityOptions: SelectOptions = createFromNumericEnum(
  ContentPriority,
  contentPriorityDisplayNames,
  [],
  doNotSort,
);

export const MetadataKeyLabelMap = new Map<string, string>([
  ['awsServices', 'AWS Service'],
  ['contentType', 'Content Type'],
  ['isvs', 'Independent Software Vendor'],
  ['lifeCycle', 'Lifecycle'],
  ['patternAccreditation', 'Pattern Accreditation'],
  ['patternProvenance', 'Pattern Provenance'],
  ['environment', 'Environment'],
  ['rType', 'RType'],
  ['technologies', 'Technology'],
  ['techDomains', 'Tech Domain'],
  ['workload', 'Workload'],
]);

export const MetadataKeyOptionsMap = new Map<string, SelectOptions>([
  ['lifeCycle', LifeCycleOptions],
  ['patternAccreditation', PatternAccreditationOptions],
  ['isvs', IndependentSoftwareVendorOptions],
  ['technologies', TechnologyOptions],
  ['patternProvenance', PatternProvenanceOptions],
  ['environment', EnvironementOptions],
  ['contentType', ContentTypeOptions],
  ['rType', RTypeOptions],
  ['contentSubType', ContentSubTypeOptions],
  ['techDomains', TechDomainOptions],
  ['mainTechDomain', TechDomainOptions],
  ['workload', WorkloadOptions],
  ['awsServices', AWSServiceOptions],
]);

export const MetadataKeyReadonlyOptionsMap = new Map<string, SelectOptions>([
  ['lifeCycle', LifeCycleOptions],
  ['patternAccreditation', PatternAccreditationOptions],
  ['isvs', IndependentSoftwareVendorOptions],
  ['technologies', TechnologyOptions],
  ['patternProvenance', PatternProvenanceOptions],
  ['environment', EnvironementOptions],
  ['contentType', ContentTypeOptions],
  ['rType', RTypeOptions],
  ['contentSubType', ContentSubTypeOptions],
  ['techDomains', TechDomainOptions],
  ['mainTechDomain', TechDomainOptions],
  ['workload', WorkloadOptions],
  ['awsServices', AWSServiceReadonlyOptions],
]);

export const ContentTemplateMap = new Map<string, Map<number, any>>([
  [
    ContentTemplateType.MigrationPattern,
    new Map<number, any>([
      [1, MigrationPatternSchemaV1],
      [2, MigrationPatternSchemaV2],
    ]),
  ],
  [
    ContentTemplateType.CloudNativePattern,
    new Map<number, any>([
      [1, PatternSchemaV1],
      [2, PatternSchemaV2],
    ]),
  ],
  [
    ContentTemplateType.ProcessPattern,
    new Map<number, any>([
      [1, PatternSchemaV1],
      [2, PatternSchemaV2],
    ]),
  ],
  [
    ContentTemplateType.OptimizationPattern,
    new Map<number, any>([
      [1, PatternSchemaV1],
      [2, PatternSchemaV2],
    ]),
  ],
  [
    ContentTemplateType.GovernancePattern,
    new Map<number, any>([
      [1, PatternSchemaV1],
      [2, PatternSchemaV2],
    ]),
  ],
  [
    ContentTemplateType.SecurityAndCompliancePattern,
    new Map<number, any>([
      [1, PatternSchemaV1],
      [2, PatternSchemaV2],
    ]),
  ],
  [ContentTemplateType.MigrationGuide, new Map<number, any>([[1, MigrationGuideSchemaV1]])],
  [ContentTemplateType.MigrationStrategy, new Map<number, any>([[1, MigrationStrategySchemaV1]])],
]);

// NEED UPDATE for all Content Sub Types
export const ContentTypeToTemplateMap = new Map<ContentSubType, ContentTemplateType>([
  [ContentSubType.PatternMigration, ContentTemplateType.MigrationPattern],
  [ContentSubType.PatternCloudNative, ContentTemplateType.CloudNativePattern],
  [ContentSubType.PatternProcess, ContentTemplateType.ProcessPattern],
  [ContentSubType.PatternOptimization, ContentTemplateType.OptimizationPattern],
  [ContentSubType.PatternGovernance, ContentTemplateType.GovernancePattern],
  [ContentSubType.PatternSecurityAndCompliance, ContentTemplateType.SecurityAndCompliancePattern],
  [ContentSubType.GuideMigration, ContentTemplateType.MigrationGuide],
  [ContentSubType.StrategyMigration, ContentTemplateType.MigrationStrategy],
]);

// NEED UPDATE for all Template types
export const CurrentTemplateVersionMap = new Map<ContentTemplateType, number>([
  [ContentTemplateType.MigrationPattern, 1],
  [ContentTemplateType.CloudNativePattern, 1],
  [ContentTemplateType.ProcessPattern, 1],
  [ContentTemplateType.OptimizationPattern, 1],
  [ContentTemplateType.GovernancePattern, 1],
  [ContentTemplateType.SecurityAndCompliancePattern, 1],
  [ContentTemplateType.MigrationGuide, 1],
  [ContentTemplateType.MigrationStrategy, 1],
]);

export const DefaultContentSubTypeMap = new Map<ContentType, ContentSubType>([
  [ContentType.Pattern, ContentSubType.PatternMigration],
  [ContentType.Guide, ContentSubType.GuideMigration],
  [ContentType.Strategy, ContentSubType.StrategyMigration],
]);

export enum TemplatePageType {
  SinglePage = 'SinglePage',
  MultiPage = 'MultiPage',
}

export const ContentTypeToPageTypeMap = new Map<ContentType, TemplatePageType>([
  [ContentType.Pattern, TemplatePageType.SinglePage],
  [ContentType.Guide, TemplatePageType.MultiPage],
  [ContentType.Strategy, TemplatePageType.MultiPage],
]);

export const ContentConversionSubTypeMap = new Map<ContentType, ContentSubType>([
  [ContentType.Pattern, ContentSubType.PatternCloudNative],
  [ContentType.Guide, ContentSubType.GuideMigration],
  [ContentType.Strategy, ContentSubType.StrategyMigration],
]);

export const publicationStateVisibilityValues = new Map<WorkflowState, string>([
  [WorkflowState.PUBLISHED, 'AWS'],
  [WorkflowState.FINALIZING, 'AWS'],
  [WorkflowState.FINALIZED, 'AWS'],
  [WorkflowState.REVOKED, 'AWS'],
  [WorkflowState.DISTRIBUTED, 'Public'],
]);

export const draftWorkflowTags = [
  WorkflowTag.INFRINGED,
  WorkflowTag.TEST,
  WorkflowTag.NEEDS_IAM_REVIEW,
  WorkflowTag.CUSTOM_XML,
  WorkflowTag.EKS_PILOT,
  WorkflowTag.SOLUTIONS_PILOT,
];

export const publicationWorkflowTags = [
  WorkflowTag.MOVED_TO_SUGGESTIONS,
  WorkflowTag.UPDATE_IN_PROGRESS,
  WorkflowTag.WAITING_FOR_UPDATE,
  WorkflowTag.NEEDS_IAM_REVIEW,
  WorkflowTag.CUSTOM_XML,
  WorkflowTag.EKS_PILOT,
  WorkflowTag.SOLUTIONS_PILOT,
];

export const workflowTagOptionsMap: SelectOptions = createFromEnum(
  WorkflowTag,
  workflowTagDisplayName,
);
